import { useMemo } from "react";

import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { HistogramChart } from "@/components/Charts/HistogramChart";
import { dataColors } from "@/components/Charts/theme";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Stat } from "@/components/Stat/Stat";
import { Stack } from "@/design-system/Stack";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { AnalyticsSection } from "@/modules/analytics/components/AnalyticsSection";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { LineChart } from "@/modules/analytics/components/LineChart";
import { ANALYTICS_AUDIENCE_SCOPE_COLORS } from "@/modules/analytics/constants";
import type {
  CommunityFeedAnalyticsPollParticipationBucket,
  CommunityFeedAnalyticsResponse,
} from "@/queries/analytics/community-feed-reporting-types";
import { useTranslation } from "@/translations";

interface PollAnalyticsSectionProps {
  polls: CommunityFeedAnalyticsResponse["polls"];
  hasProjectConnections: boolean;
}

/**
 * Renders aggregate Community Feed poll analytics and includes the connection comparison only for connected projects.
 */
export function PollAnalyticsSection({ polls, hasProjectConnections }: PollAnalyticsSectionProps): React.ReactNode {
  const { t } = useTranslation();
  const help = useMetricHelp();
  const residentPollData = useMemo(
    () =>
      polls.monthly.map((point) => ({
        x: point.x,
        y: point.residentPolls,
        isPending: point.isCurrentMonth,
      })),
    [polls.monthly],
  );
  const projectTeamPollData = useMemo(
    () =>
      polls.monthly.map((point) => ({
        x: point.x,
        y: point.projectTeamPolls,
        isPending: point.isCurrentMonth,
      })),
    [polls.monthly],
  );
  const pollResponseData = useMemo(
    () => [
      {
        label: t("page.analytics-community-feed.polls.responses"),
        color: dataColors.green,
        dataPoints: polls.monthly.map((point) => ({
          x: point.x,
          y: point.responses,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [polls.monthly, t],
  );
  const pollParticipationData = useMemo(
    () => [
      {
        label: t("page.analytics-community-feed.polls.participation-rate"),
        color: dataColors.mint,
        dataPoints: polls.monthly.flatMap((point) =>
          point.participationRatePercentage === undefined
            ? []
            : [
                {
                  x: point.x,
                  y: point.participationRatePercentage,
                  isPending: point.isCurrentMonth,
                },
              ],
        ),
      },
    ],
    [polls.monthly, t],
  );
  const pollParticipationDistributionData = useMemo(
    () =>
      polls.participationDistribution.map((point) => ({
        category: point.x as CommunityFeedAnalyticsPollParticipationBucket,
        label: t(
          `page.analytics-community-feed.polls.participation-bucket.${point.x as CommunityFeedAnalyticsPollParticipationBucket}`,
        ),
        value: point.y,
      })),
    [polls.participationDistribution, t],
  );
  const pollAudienceData = useMemo(
    () =>
      polls.audienceScopes.map((scope, index) => ({
        label:
          scope.type === "projectOnly"
            ? t("page.analytics-community-feed.connections.project-only")
            : (scope.projectConnection?.name ?? t("page.analytics-community-feed.connections.project-only")),
        color: ANALYTICS_AUDIENCE_SCOPE_COLORS[index % ANALYTICS_AUDIENCE_SCOPE_COLORS.length],
        dataPoints: scope.dataPoints.map((point) => ({
          x: point.x,
          y: point.residentPolls + point.projectTeamPolls,
          isPending: point.isCurrentMonth,
        })),
      })),
    [polls.audienceScopes, t],
  );

  return (
    <PageGrid.Column size="100%">
      <AnalyticsSection
        title={t("page.analytics-community-feed.polls.section-title")}
        description={t("page.analytics-community-feed.polls.description")}
      >
        <AnalyticsCard.Root>
          <AnalyticsCard.Header
            title={t("page.analytics-community-feed.polls.overview-title")}
            tooltip={help("page.analytics-community-feed.polls.overview-title.tooltip")}
          />
          <section className="flex flex-col gap-space-md @xs:flex-row @xs:flex-wrap">
            <Stat label={t("page.analytics-community-feed.polls.total")} value={polls.summary.polls} />
            <Stat label={t("page.analytics-community-feed.polls.open")} value={polls.summary.openPolls} />
            <Stat
              label={t("page.analytics-community-feed.polls.responses")}
              value={polls.summary.responses}
              renderFooter={() =>
                t("page.analytics-community-feed.polls.results-available", {
                  available: polls.summary.resultAvailablePolls,
                  total: polls.summary.polls,
                })
              }
            />
            <Stat
              label={t("page.analytics-community-feed.polls.unique-respondents")}
              value={polls.summary.uniqueRespondents}
            />
            <Stat
              label={t("page.analytics-community-feed.polls.participation-rate")}
              value={polls.summary.participationRatePercentage}
              renderValue={(value) => (value === undefined ? "-" : `${value.toFixed(1)}%`)}
            />
            <Stat
              label={t("page.analytics-community-feed.polls.without-response")}
              value={polls.summary.pollsWithoutResponses}
            />
          </section>
        </AnalyticsCard.Root>
        <AnalyticsCard.Root variant="graph" height="content">
          <AnalyticsCard.Header
            title={t("page.analytics-community-feed.polls.ownership-title")}
            tooltip={help("page.analytics-community-feed.polls.ownership-title.tooltip")}
          />
          <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
            <BarChart
              dataPrimary={{ data: residentPollData }}
              dataSecondary={{ data: projectTeamPollData, color: dataColors.green }}
              xAxisType="date"
              yLabel={t("page.analytics-community-feed.axis.polls")}
              yTickMode="integer"
              hoverLabel={(bar) => (
                <Stack gap="xs">
                  <span>
                    {t("page.analytics-community-feed.polls.resident-created")}: {bar.primary?.y ?? 0}
                  </span>
                  <span>
                    {t("page.analytics-community-feed.polls.project-team-created")}: {bar.secondary?.y ?? 0}
                  </span>
                  {(bar.primary?.isPending || bar.secondary?.isPending) && (
                    <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                  )}
                </Stack>
              )}
            />
            <ChartLegend
              items={[
                {
                  label: t("page.analytics-community-feed.polls.resident-created"),
                  color: dataColors.primary,
                },
                {
                  label: t("page.analytics-community-feed.polls.project-team-created"),
                  color: dataColors.green,
                },
                {
                  label: t("page.analytics-details.engagement.chart.legend.current-period"),
                  color: dataColors.primary,
                  pattern: "striped",
                },
              ]}
            />
          </figure>
        </AnalyticsCard.Root>
        <AnalyticsCard.Root variant="graph" height="content">
          <AnalyticsCard.Header
            title={t("page.analytics-community-feed.polls.responses-title")}
            tooltip={help("page.analytics-community-feed.polls.responses-title.tooltip")}
          />
          <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
            <LineChart
              data={pollResponseData}
              xAxisType="date"
              yLabel={t("page.analytics-community-feed.axis.responses")}
              hoverLabel={(point) => (
                <Stack gap="xs">
                  <span>{point.relatedPoints[0].dataPoint.y}</span>
                  {point.relatedPoints[0].dataPoint.isPending && (
                    <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                  )}
                </Stack>
              )}
            />
            <ChartLegend items={[{ label: pollResponseData[0].label, color: dataColors.green }]} />
          </figure>
        </AnalyticsCard.Root>
        <AnalyticsCard.Root variant="graph" height="content">
          <AnalyticsCard.Header
            title={t("page.analytics-community-feed.polls.participation-title")}
            tooltip={help("page.analytics-community-feed.polls.participation-title.tooltip")}
          />
          <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
            <LineChart
              data={pollParticipationData}
              xAxisType="date"
              yLabel={t("page.analytics-community-feed.axis.participation-rate")}
              formatYTick={(value) => `${value}%`}
              hoverLabel={(point) => (
                <Stack gap="xs">
                  <span>{point.relatedPoints[0].dataPoint.y}%</span>
                  {point.relatedPoints[0].dataPoint.isPending && (
                    <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                  )}
                </Stack>
              )}
            />
            <ChartLegend items={[{ label: pollParticipationData[0].label, color: dataColors.mint }]} />
          </figure>
        </AnalyticsCard.Root>
        <AnalyticsCard.Root height="content">
          <AnalyticsCard.Header
            title={t("page.analytics-community-feed.polls.participation-distribution-title")}
            tooltip={help("page.analytics-community-feed.polls.participation-distribution-title.tooltip")}
          />
          <HistogramChart data={pollParticipationDistributionData} />
        </AnalyticsCard.Root>
        {hasProjectConnections && (
          <AnalyticsCard.Root variant="graph" height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-community-feed.polls.connections-title")}
              tooltip={help("page.analytics-community-feed.polls.connections-title.tooltip")}
            />
            <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
              <LineChart
                data={pollAudienceData}
                xAxisType="date"
                yLabel={t("page.analytics-community-feed.axis.polls")}
                hoverLabel={(point) => (
                  <ul className="flex flex-col gap-space-xs">
                    {point.relatedPoints.map((relatedPoint) => (
                      <li key={relatedPoint.label}>
                        {relatedPoint.label}: {relatedPoint.dataPoint.y}
                      </li>
                    ))}
                    {point.relatedPoints.some((relatedPoint) => relatedPoint.dataPoint.isPending) && (
                      <li>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</li>
                    )}
                  </ul>
                )}
              />
              <ChartLegend items={pollAudienceData.map((series) => ({ label: series.label, color: series.color }))} />
            </figure>
          </AnalyticsCard.Root>
        )}
      </AnalyticsSection>
    </PageGrid.Column>
  );
}
